import PropTypes from 'prop-types';

export const enableInMarketDate = process.env.REACT_APP_ENABLE_IN_MARKET_DATE === 'true';
export const enableSeasonalForecast = process.env.REACT_APP_ENABLE_SEASONAL_FORECAST === 'true';

export const SETTINGS_GRID_CELL_PADDING = '0 6px';

// Region/District/Division header rows span every column before the date pickers,
// which are rendered as their own separate TableCells after the spanning cell.
export const SETTINGS_GRID_SPANNED_COLUMN_COUNT = enableSeasonalForecast ? 8 : 7;

// Single source of truth for the DistributorSettings SKU grid and the
// ProductSettings distributor grid, which share an identical column layout.
// Order MUST match the left-to-right TableCell order rendered in each
// grid's fixedHeaderContent() and each row component: a <colgroup>'s <col>s
// apply to <td>s purely by position, so any extraColumns (below) must be
// spliced into this list at the same spot their TableCell is rendered, not
// just appended at the end.
//
// These are relative shares, normalised to percentages below, so a column hidden
// by a feature flag hands its share to the others and the widths always total
// 100%. Both grids use tableLayout: 'fixed', where any leftover width inflates an
// unsized column and any overflow collapses one, so no column may go unsized.
// The row select checkbox lives inside the 'primary' cell rather than in a column
// of its own, which is what keeps it from ever being squeezed under the name.
const SETTINGS_GRID_COLUMN_WEIGHTS_BEFORE_EXTRA = [
    { key: 'primary', weight: 26 },     // checkbox + "All SKUs" / "All Distributors"
    { key: 'legal', weight: 5 },
    { key: 'palletQuantity', weight: 7 },
    { key: 'palletDivisor', weight: 7 },
    { key: 'unitWeight', weight: 7 },
    { key: 'offeringStatus', weight: 9.5 },
    { key: 'fulfillmentMethod', weight: 9.5 },
];

const SETTINGS_GRID_COLUMN_WEIGHTS_AFTER_EXTRA = [
    ...(enableSeasonalForecast ? [{ key: 'seasonal', weight: 7 }] : []),
    // The date pickers need the widest share of any control: MM/DD/YYYY plus the
    // calendar and clear buttons, or the year gets clipped.
    ...(enableInMarketDate ? [{ key: 'inMarketDate', weight: 12 }] : []),
    ...(enableInMarketDate && enableSeasonalForecast ? [{ key: 'endInMarketDate', weight: 13 }] : []),
];

const SETTINGS_GRID_COLUMN_WEIGHTS = [
    ...SETTINGS_GRID_COLUMN_WEIGHTS_BEFORE_EXTRA,
    ...SETTINGS_GRID_COLUMN_WEIGHTS_AFTER_EXTRA,
];

const weightsToColumns = (weights) => {
    const totalWeight = weights.reduce((total, col) => total + col.weight, 0);

    return weights.map(col => ({
        key: col.key,
        width: `${(col.weight / totalWeight * 100).toFixed(4)}%`,
    }));
};

export const SETTINGS_GRID_COLUMNS = weightsToColumns(SETTINGS_GRID_COLUMN_WEIGHTS);

// extraColumns lets one grid add a column (e.g. Customer Part # on DistributorSettings only)
// without affecting the other grid, which keeps calling this with no arguments. Its TableCell
// renders right after 'fulfillmentMethod' and before 'seasonal'/the date columns, so it is
// spliced into that same spot here to keep <col> order matching <td> order.
export const SettingsGridColGroup = ({ extraColumns = [] }) => {
    const columns = extraColumns.length
        ? weightsToColumns([
            ...SETTINGS_GRID_COLUMN_WEIGHTS_BEFORE_EXTRA,
            ...extraColumns,
            ...SETTINGS_GRID_COLUMN_WEIGHTS_AFTER_EXTRA,
        ])
        : SETTINGS_GRID_COLUMNS;

    return (
        <colgroup>
            {columns.map(col => (
                <col key={col.key} style={{ width: col.width }} />
            ))}
        </colgroup>
    );
};

SettingsGridColGroup.propTypes = {
    extraColumns: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string,
        weight: PropTypes.number,
    })),
};
